/**
 * What a person can do to one term of a query, as pure functions on the term:
 * the cycles the chip and the keys walk through, and the edits that keep a term
 * the way the search expects it. Every edit returns a new object, and the same
 * object when nothing changes. `caseSensitive` and `disabled` are set only
 * while they are on, like the parser writes them.
 */
import { hasWhiteSpace, type Match, type Occur, type TextQueryTerm } from '../lib/textQuery';

/** The occurrences in the order a click on the head walks through them. */
export const OCCUR_CYCLE: readonly Occur[] = ['must', 'mustNot', 'should'];

/** The match modes in the order the `m` key walks through them. */
export const MATCH_CYCLE: readonly Match[] = ['contains', 'startsWith', 'endsWith', 'phrase'];

/** The occurrence after `occur`: required, excluded, any-of, required again. */
export function nextOccur(occur: Occur): Occur {
  return OCCUR_CYCLE[(OCCUR_CYCLE.indexOf(occur) + 1) % OCCUR_CYCLE.length]!;
}

/**
 * Whether a text can have the match. A text with white space in it is a phrase
 * and nothing else: the search refuses white space in any other term.
 */
export function matchAllowed(text: string, match: Match): boolean {
  return match === 'phrase' || !hasWhiteSpace(text);
}

/** The match after the term's own, skipping what its text cannot have. */
export function nextMatch(term: Pick<TextQueryTerm, 'text' | 'match'>): Match {
  const start = MATCH_CYCLE.indexOf(term.match);
  for (let step = 1; step <= MATCH_CYCLE.length; step += 1) {
    const candidate = MATCH_CYCLE[(start + step) % MATCH_CYCLE.length]!;
    if (matchAllowed(term.text, candidate)) return candidate;
  }
  return term.match;
}

/** The term with another occurrence. */
export function withOccur(term: TextQueryTerm, occur: Occur): TextQueryTerm {
  return term.occur === occur ? term : { ...term, occur };
}

/** The term with another match mode; one its text cannot have is refused. */
export function withMatch(term: TextQueryTerm, match: Match): TextQueryTerm {
  if (term.match === match || !matchAllowed(term.text, match)) return term;
  return { ...term, match };
}

/** The term with case counting or not. */
export function withCaseSensitive(term: TextQueryTerm, on: boolean): TextQueryTerm {
  if ((term.caseSensitive === true) === on) return term;
  const edited = { ...term };
  if (on) edited.caseSensitive = true;
  else delete edited.caseSensitive;
  return edited;
}

/** The term switched off (kept, but not searched) or on again. */
export function withDisabled(term: TextQueryTerm, on: boolean): TextQueryTerm {
  if ((term.disabled === true) === on) return term;
  const edited = { ...term };
  if (on) edited.disabled = true;
  else delete edited.disabled;
  return edited;
}
